import { Building2, ShieldX } from "lucide-react";
import { Navigate, useNavigate } from "@tanstack/react-router";

import { useCurrentUser } from "~/lib/auth";
import { lookupCountry } from "~/lib/countries";
import { type TenantRecord, useTenants } from "~/lib/tenants";

import { CreateTenantDialog } from "~/components/CreateTenantDialog";
import { TableSkeleton } from "~/components/TableSkeleton";
import { TenantRowActions } from "~/components/TenantRowActions";

const STATUS_PILL: Record<TenantRecord["status"], string> = {
    active: "status-pill--live",
    suspended: "status-pill--paused",
    archived: "status-pill--queued",
};

export function TenantsPage(): React.ReactElement {
    const me = useCurrentUser();
    const isAdmin = me.data?.roles.includes("admin");
    const { data: tenants, isLoading, isError, error } = useTenants();
    const navigate = useNavigate();

    if (me.data && !isAdmin) {
        return <Navigate to="/" />;
    }

    const activeCount = tenants?.filter((t) => t.status === "active").length ?? 0;

    return (
        <div className="stack-lg">
            <header className="page-header">
                <div className="page-header__top">
                    <div className="page-header__heading">
                        <span className="page-header__eyebrow">
                            <span className="page-header__eyebrow__icon"><Building2 size={14} aria-hidden /></span>
                            Identity &amp; access
                        </span>
                        <h1 className="page-header__title">Tenants</h1>
                        <p className="page-header__description">
                            Customer organizations that consume the platform. Each tenant scopes its own
                            users, observations, click logs, and reports. Internal staff (Admin / Operator /
                            Analyst) live outside any tenant.
                        </p>
                    </div>
                    <div className="page-header__actions">
                        <CreateTenantDialog />
                    </div>
                </div>
                <div className="page-header__meta">
                    <span className="page-header__meta__item tabular-nums">
                        {(tenants?.length ?? 0).toLocaleString()} total
                    </span>
                    {activeCount > 0 && (
                        <span className="page-header__meta__item tabular-nums" style={{ color: "var(--color-success)" }}>
                            {activeCount} active
                        </span>
                    )}
                </div>
            </header>

            <section className="section-card" aria-label="Tenants">
                {isLoading && <TableSkeleton rows={6} columns={7} />}
                {isError && (
                    <div className="empty-state">
                        <ShieldX size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">Could not load tenants</div>
                        <div className="empty-state__body">
                            {error instanceof Error ? error.message : "Unknown error"}
                        </div>
                    </div>
                )}
                {tenants && tenants.length === 0 && (
                    <div className="empty-state">
                        <Building2 size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">No tenants yet</div>
                        <div className="empty-state__body">
                            Create the first one above. Tenants come into play once customer-role users land
                            and once licenses (Phase 1B.3) start carrying tenant-scoped quota.
                        </div>
                    </div>
                )}
                {tenants && tenants.length > 0 && (
                    <div style={{ overflowX: "auto" }}>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Slug</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Country</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Status</th>
                                    <th style={{ textAlign: "right", whiteSpace: "nowrap", width: 1 }}>Users</th>
                                    <th style={{ textAlign: "right", whiteSpace: "nowrap", width: 1 }}>Active licenses</th>
                                    <th style={{ whiteSpace: "nowrap", width: 1 }}>Created</th>
                                    <th style={{ width: 1 }} aria-label="Actions" />
                                </tr>
                            </thead>
                            <tbody>
                                {tenants.map((t) => {
                                    const country = lookupCountry(t.country);
                                    return (
                                    <tr
                                        key={t.id}
                                        onClick={() => navigate({ to: "/tenants/$tenantId", params: { tenantId: t.id } })}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <td className="text-13">{t.name}</td>
                                        <td>
                                            <code
                                                className="type-mono-sm"
                                                style={{
                                                    background: "var(--color-surface-muted)",
                                                    padding: "2px 6px",
                                                    borderRadius: "var(--radius-sm)",
                                                }}
                                            >
                                                {t.slug}
                                            </code>
                                        </td>
                                        <td style={{ whiteSpace: "nowrap" }} className="text-12 text-text-muted">
                                            {country ? (
                                                <span className="cluster-xs">
                                                    <span aria-hidden>{country.flag}</span>
                                                    <span>{country.code}</span>
                                                </span>
                                            ) : t.country ? (
                                                <span>{t.country}</span>
                                            ) : (
                                                <span className="text-text-subtle">—</span>
                                            )}
                                        </td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            <span className={`status-pill ${STATUS_PILL[t.status]}`}>
                                                {t.status}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: "right" }} className="text-13 text-text-muted tabular">
                                            {t.userCount}
                                        </td>
                                        <td style={{ textAlign: "right" }} className="text-13 text-text-muted tabular">
                                            {t.activeLicenseCount}
                                        </td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            <span className="text-12 text-text-muted">
                                                {new Date(t.createdAt).toLocaleDateString(undefined, {
                                                    year: "numeric",
                                                    month: "short",
                                                    day: "numeric",
                                                })}
                                            </span>
                                        </td>
                                        <td
                                            style={{ textAlign: "right" }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <TenantRowActions tenant={t} />
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}
